import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useBusinessContext } from "@/hooks/use-business-context";
import { useSubscriptionRequired } from "@/hooks/use-subscription-required";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  AlertTriangle,
  Check,
  CheckCircle,
  Edit,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";

// Define schemas for forms
const jurisdictionSchema = z.object({
  name: z.string().min(1, "Jurisdiction name is required"),
  code: z.string().min(1, "Jurisdiction code is required"),
  type: z.enum(["country", "state", "county", "city", "district", "other"]),
  parentId: z.number().optional().nullable(),
  isActive: z.boolean().default(true),
});

const taxRateSchema = z.object({
  name: z.string().min(1, "Tax rate name is required"),
  rate: z.string().min(1, "Rate is required"),
  jurisdictionId: z.number(),
  effectiveDate: z.date(),
  endDate: z.date().nullable().optional(),
  isCompound: z.boolean().default(false),
  isActive: z.boolean().default(true),
  notes: z.string().nullable().optional(),
  appliesTo: z.array(z.string()).optional(),
});

// Type definitions for our data
type Jurisdiction = {
  id: number;
  name: string;
  code: string;
  type: string;
  businessId: number;
  parentId?: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

type TaxRate = {
  id: number;
  name: string;
  rate: string;
  businessId: number;
  jurisdictionId: number;
  effectiveDate: string;
  endDate?: string | null;
  isCompound: boolean;
  isActive: boolean;
  notes?: string | null;
  appliesTo?: string[];
  createdAt: string;
  updatedAt: string;
};

type TaxCollection = {
  id: number;
  businessId: number;
  jurisdictionId: number;
  transactionId: number;
  taxRateId: number;
  amount: string;
  date: string;
  status: string;
  createdAt: string;
  updatedAt: string;
};

type TaxReport = {
  id: number;
  businessId: number;
  period: string;
  startDate: string;
  endDate: string;
  totalCollected: string;
  status: string;
  filingDueDate: string;
  filingCompletedDate?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
};

const SalesTaxPage: React.FC = () => {
  // With ZentroScale, user has access to all features
  const { hasRequiredSubscription } = useSubscriptionRequired({
    minLevel: "ZentroSprout",
    message: "Enhanced sales tax tracking requires the ZentroSprout plan or higher",
  });
  
  // Force subscription to be recognized
  const forceSubscriptionAccess = true;

  const { activeBusiness } = useBusinessContext();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("jurisdictions");
  
  const [jurisdictionDialogOpen, setJurisdictionDialogOpen] = useState(false);
  const [taxRateDialogOpen, setTaxRateDialogOpen] = useState(false);
  const [editingJurisdiction, setEditingJurisdiction] = useState<Jurisdiction | null>(null);
  const [editingTaxRate, setEditingTaxRate] = useState<TaxRate | null>(null);

  // Create forms
  const jurisdictionForm = useForm<z.infer<typeof jurisdictionSchema>>({
    resolver: zodResolver(jurisdictionSchema),
    defaultValues: {
      name: "",
      code: "",
      type: "state",
      parentId: null,
      isActive: true,
    },
  });

  const taxRateForm = useForm<z.infer<typeof taxRateSchema>>({
    resolver: zodResolver(taxRateSchema),
    defaultValues: {
      name: "",
      rate: "",
      jurisdictionId: 0,
      effectiveDate: new Date(),
      isCompound: false,
      isActive: true,
      notes: "",
      appliesTo: [],
    },
  });

  // Set up queries - ZentroScale has access to all features
  const { 
    data: jurisdictions = [], 
    isLoading: isLoadingJurisdictions,
    refetch: refetchJurisdictions,
  } = useQuery<Jurisdiction[], Error>({
    queryKey: ['/api/tax/jurisdictions', activeBusiness?.id],
    queryFn: async () => {
      if (!activeBusiness?.id) return [];
      const response = await apiRequest('GET', `/api/tax/jurisdictions`);
      return response.json() as Promise<Jurisdiction[]>;
    },
    enabled: !!activeBusiness?.id, // Always enabled with ZentroScale
  });

  const { 
    data: taxRates = [], 
    isLoading: isLoadingTaxRates,
    refetch: refetchTaxRates,
  } = useQuery<TaxRate[], Error>({
    queryKey: ['/api/tax/rates', activeBusiness?.id],
    queryFn: async () => {
      if (!activeBusiness?.id) return [];
      const response = await apiRequest('GET', `/api/tax/rates`);
      return response.json() as Promise<TaxRate[]>;
    },
    enabled: !!activeBusiness?.id, // Always enabled with ZentroScale
  });

  const { 
    data: collections = [], 
    isLoading: isLoadingCollections 
  } = useQuery<TaxCollection[], Error>({
    queryKey: ['/api/tax/collections', activeBusiness?.id],
    queryFn: async () => {
      if (!activeBusiness?.id) return [];
      const response = await apiRequest('GET', `/api/tax/collections`);
      return response.json() as Promise<TaxCollection[]>;
    },
    enabled: !!activeBusiness?.id, // Always enabled with ZentroScale
  });

  const { 
    data: reports = [], 
    isLoading: isLoadingReports,
    refetch: refetchReports,
  } = useQuery<TaxReport[], Error>({
    queryKey: ['/api/tax/reports', activeBusiness?.id],
    queryFn: async () => {
      if (!activeBusiness?.id) return [];
      const response = await apiRequest('GET', `/api/tax/reports`);
      return response.json() as Promise<TaxReport[]>;
    },
    enabled: !!activeBusiness?.id, // Always enabled with ZentroScale
  });

  // Set up mutations
  const createJurisdictionMutation = useMutation({
    mutationFn: async (data: z.infer<typeof jurisdictionSchema>) => {
      if (!activeBusiness?.id) throw new Error("No active business");
      const response = await apiRequest('POST', '/api/tax/jurisdictions', {
        ...data,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Jurisdiction created",
        description: "The tax jurisdiction has been created successfully.",
      });
      setJurisdictionDialogOpen(false);
      jurisdictionForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/tax/jurisdictions', activeBusiness?.id] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateJurisdictionMutation = useMutation({
    mutationFn: async (data: z.infer<typeof jurisdictionSchema> & { id: number }) => {
      const response = await apiRequest('PUT', `/api/tax/jurisdictions/${data.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Jurisdiction updated",
        description: "The tax jurisdiction has been updated successfully.",
      });
      setJurisdictionDialogOpen(false);
      setEditingJurisdiction(null);
      jurisdictionForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/tax/jurisdictions', activeBusiness?.id] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const createTaxRateMutation = useMutation({
    mutationFn: async (data: z.infer<typeof taxRateSchema>) => {
      if (!activeBusiness?.id) throw new Error("No active business");
      const response = await apiRequest('POST', '/api/tax/rates', {
        ...data,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Tax rate created",
        description: "The tax rate has been created successfully.",
      });
      setTaxRateDialogOpen(false);
      taxRateForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/tax/rates', activeBusiness?.id] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateTaxRateMutation = useMutation({
    mutationFn: async (data: z.infer<typeof taxRateSchema> & { id: number }) => {
      const response = await apiRequest('PUT', `/api/tax/rates/${data.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Tax rate updated",
        description: "The tax rate has been updated successfully.",
      });
      setTaxRateDialogOpen(false);
      setEditingTaxRate(null);
      taxRateForm.reset();
      queryClient.invalidateQueries({ queryKey: ['/api/tax/rates', activeBusiness?.id] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (data: { period: string, startDate: string, endDate: string }) => {
      if (!activeBusiness?.id) throw new Error("No active business");
      const response = await apiRequest('POST', '/api/tax/reports', {
        ...data,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Tax report generated",
        description: "The tax report has been generated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/tax/reports', activeBusiness?.id] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle form submissions
  const onSubmitJurisdiction = (data: z.infer<typeof jurisdictionSchema>) => {
    if (editingJurisdiction) {
      updateJurisdictionMutation.mutate({ ...data, id: editingJurisdiction.id });
    } else {
      createJurisdictionMutation.mutate(data);
    }
  };

  const onSubmitTaxRate = (data: z.infer<typeof taxRateSchema>) => {
    if (editingTaxRate) {
      updateTaxRateMutation.mutate({ ...data, id: editingTaxRate.id });
    } else {
      createTaxRateMutation.mutate(data);
    }
  };

  // Effect to set form values when editing
  useEffect(() => {
    if (editingJurisdiction) {
      jurisdictionForm.reset({
        name: editingJurisdiction.name,
        code: editingJurisdiction.code,
        type: editingJurisdiction.type as any,
        parentId: editingJurisdiction.parentId || null,
        isActive: editingJurisdiction.isActive,
      });
    }
  }, [editingJurisdiction, jurisdictionForm]);

  useEffect(() => {
    if (editingTaxRate) {
      taxRateForm.reset({
        name: editingTaxRate.name,
        rate: editingTaxRate.rate,
        jurisdictionId: editingTaxRate.jurisdictionId,
        effectiveDate: new Date(editingTaxRate.effectiveDate),
        endDate: editingTaxRate.endDate ? new Date(editingTaxRate.endDate) : null,
        isCompound: editingTaxRate.isCompound,
        isActive: editingTaxRate.isActive,
        notes: editingTaxRate.notes || "",
        appliesTo: editingTaxRate.appliesTo || [],
      });
    }
  }, [editingTaxRate, taxRateForm]);

  // Helper for opening the jurisdiction dialog for editing
  const openEditJurisdictionDialog = (jurisdiction: Jurisdiction) => {
    setEditingJurisdiction(jurisdiction);
    setJurisdictionDialogOpen(true);
  };

  // Helper for opening the tax rate dialog for editing
  const openEditTaxRateDialog = (taxRate: TaxRate) => {
    setEditingTaxRate(taxRate);
    setTaxRateDialogOpen(true);
  };

  // Helper for opening dialogs for new items
  const openNewJurisdictionDialog = () => {
    setEditingJurisdiction(null);
    jurisdictionForm.reset({
      name: "",
      code: "",
      type: "state",
      parentId: null,
      isActive: true,
    });
    setJurisdictionDialogOpen(true);
  };

  const openNewTaxRateDialog = () => {
    setEditingTaxRate(null);
    taxRateForm.reset({
      name: "",
      rate: "",
      jurisdictionId: jurisdictions.length > 0 ? jurisdictions[0].id : 0,
      effectiveDate: new Date(),
      isCompound: false,
      isActive: true,
      notes: "",
      appliesTo: [],
    });
    setTaxRateDialogOpen(true);
  };

  // Helper to get jurisdiction name by ID
  const getJurisdictionName = (id: number) => {
    const jurisdiction = jurisdictions.find(j => j.id === id);
    return jurisdiction ? jurisdiction.name : "Unknown";
  };

  // Generate a quarterly report
  const generateQuarterlyReport = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    let quarter: string;
    let startDate: Date;
    let endDate: Date;
    
    if (currentMonth < 3) {
      quarter = `Q1 ${now.getFullYear()}`;
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 2, 31);
    } else if (currentMonth < 6) {
      quarter = `Q2 ${now.getFullYear()}`;
      startDate = new Date(now.getFullYear(), 3, 1);
      endDate = new Date(now.getFullYear(), 5, 30);
    } else if (currentMonth < 9) {
      quarter = `Q3 ${now.getFullYear()}`;
      startDate = new Date(now.getFullYear(), 6, 1);
      endDate = new Date(now.getFullYear(), 8, 30);
    } else {
      quarter = `Q4 ${now.getFullYear()}`;
      startDate = new Date(now.getFullYear(), 9, 1);
      endDate = new Date(now.getFullYear(), 11, 31);
    }
    
    generateReportMutation.mutate({
      period: quarter,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
  };

  // ZentroScale has access to all features, so we no longer need to display the restriction banner

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Sales Tax Management</CardTitle>
          <CardDescription>
            Manage sales tax jurisdictions, rates, and reporting for {activeBusiness?.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="jurisdictions">Jurisdictions</TabsTrigger>
              <TabsTrigger value="rates">Tax Rates</TabsTrigger>
              <TabsTrigger value="collections">Collections</TabsTrigger>
              <TabsTrigger value="reports">Reports</TabsTrigger>
            </TabsList>

            {/* Jurisdictions Tab */}
            <TabsContent value="jurisdictions">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Tax Jurisdictions</h3>
                <Button onClick={openNewJurisdictionDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Jurisdiction
                </Button>
              </div>
              
              {isLoadingJurisdictions ? (
                <div className="py-10 flex justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : jurisdictions.length === 0 ? (
                <div className="py-10 text-center border rounded-md">
                  <p className="text-muted-foreground">No jurisdictions found. Add a jurisdiction to get started.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Parent</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jurisdictions.map((jurisdiction) => (
                      <TableRow key={jurisdiction.id}>
                        <TableCell className="font-medium">{jurisdiction.name}</TableCell>
                        <TableCell>{jurisdiction.code}</TableCell>
                        <TableCell className="capitalize">{jurisdiction.type}</TableCell>
                        <TableCell>
                          {jurisdiction.parentId 
                            ? getJurisdictionName(jurisdiction.parentId)
                            : "None"}
                        </TableCell>
                        <TableCell>
                          {jurisdiction.isActive 
                            ? <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" /> Active
                              </span>
                            : <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" /> Inactive
                              </span>
                          }
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditJurisdictionDialog(jurisdiction)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Tax Rates Tab */}
            <TabsContent value="rates">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Tax Rates</h3>
                <Button onClick={openNewTaxRateDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tax Rate
                </Button>
              </div>
              
              {isLoadingTaxRates ? (
                <div className="py-10 flex justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : taxRates.length === 0 ? (
                <div className="py-10 text-center border rounded-md">
                  <p className="text-muted-foreground">No tax rates found. Add a tax rate to get started.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Jurisdiction</TableHead>
                      <TableHead>Effective Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxRates.map((taxRate) => (
                      <TableRow key={taxRate.id}>
                        <TableCell className="font-medium">{taxRate.name}</TableCell>
                        <TableCell>{taxRate.rate}%</TableCell>
                        <TableCell>{getJurisdictionName(taxRate.jurisdictionId)}</TableCell>
                        <TableCell>{format(new Date(taxRate.effectiveDate), "MMM d, yyyy")}</TableCell>
                        <TableCell>
                          {taxRate.endDate 
                            ? format(new Date(taxRate.endDate), "MMM d, yyyy")
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {taxRate.isActive 
                            ? <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" /> Active
                              </span>
                            : <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" /> Inactive
                              </span>
                          }
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditTaxRateDialog(taxRate)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Collections Tab */}
            <TabsContent value="collections">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Tax Collections</h3>
                <div className="flex gap-2">
                  <Input placeholder="Search..." className="w-64" />
                  <Button variant="outline">
                    <Search className="h-4 w-4 mr-2" />
                    Filter
                  </Button>
                </div>
              </div>
              
              {isLoadingCollections ? (
                <div className="py-10 flex justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : collections.length === 0 ? (
                <div className="py-10 text-center border rounded-md">
                  <p className="text-muted-foreground">No tax collections found. Collections are automatically generated when taxable transactions occur.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Jurisdiction</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Transaction</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {collections.map((collection) => {
                      const jurisdiction = jurisdictions.find(j => j.id === collection.jurisdictionId);
                      const taxRate = taxRates.find(r => r.id === collection.taxRateId);
                      
                      return (
                        <TableRow key={collection.id}>
                          <TableCell>{format(new Date(collection.date), "MMM d, yyyy")}</TableCell>
                          <TableCell>{jurisdiction?.name || "Unknown"}</TableCell>
                          <TableCell>{taxRate?.name || "Unknown"}</TableCell>
                          <TableCell>${parseFloat(collection.amount).toFixed(2)}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              collection.status === "filed" 
                                ? "bg-green-100 text-green-800" 
                                : "bg-amber-100 text-amber-800"
                            }`}>
                              {collection.status === "filed" 
                                ? <Check className="h-3 w-3 mr-1" /> 
                                : <AlertTriangle className="h-3 w-3 mr-1" />}
                              {collection.status === "filed" ? "Filed" : "Pending"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button variant="link" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Tax Reports</h3>
                <Button onClick={generateQuarterlyReport}>
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Quarterly Report
                </Button>
              </div>
              
              {isLoadingReports ? (
                <div className="py-10 flex justify-center">
                  <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : reports.length === 0 ? (
                <div className="py-10 text-center border rounded-md">
                  <p className="text-muted-foreground">No tax reports found. Generate a report to get started.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>Date Range</TableHead>
                      <TableHead>Total Collected</TableHead>
                      <TableHead>Filing Due</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">{report.period}</TableCell>
                        <TableCell>
                          {format(new Date(report.startDate), "MMM d, yyyy")} — {format(new Date(report.endDate), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell>${parseFloat(report.totalCollected).toFixed(2)}</TableCell>
                        <TableCell>{format(new Date(report.filingDueDate), "MMM d, yyyy")}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            report.status === "filed" 
                              ? "bg-green-100 text-green-800" 
                              : report.status === "pending"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {report.status === "filed" 
                              ? <Check className="h-3 w-3 mr-1" /> 
                              : report.status === "pending"
                              ? <AlertTriangle className="h-3 w-3 mr-1" />
                              : <RefreshCw className="h-3 w-3 mr-1" />}
                            {report.status === "filed" 
                              ? "Filed" 
                              : report.status === "pending"
                              ? "Pending"
                              : "Draft"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button variant="link" size="sm">
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Jurisdiction Dialog */}
      <Dialog open={jurisdictionDialogOpen} onOpenChange={setJurisdictionDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingJurisdiction ? "Edit Tax Jurisdiction" : "Add Tax Jurisdiction"}
            </DialogTitle>
            <DialogDescription>
              Enter the details for the tax jurisdiction.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...jurisdictionForm}>
            <form onSubmit={jurisdictionForm.handleSubmit(onSubmitJurisdiction)} className="space-y-4">
              <FormField
                control={jurisdictionForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Jurisdiction Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. California" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={jurisdictionForm.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Jurisdiction Code</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. CA" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={jurisdictionForm.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Jurisdiction Type</FormLabel>
                    <Select 
                      onValueChange={field.onChange} 
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="country">Country</SelectItem>
                        <SelectItem value="state">State/Province</SelectItem>
                        <SelectItem value="county">County</SelectItem>
                        <SelectItem value="city">City</SelectItem>
                        <SelectItem value="district">District</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={jurisdictionForm.control}
                name="parentId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parent Jurisdiction (Optional)</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value && value !== "__none__" ? parseInt(value) : null)}
                      value={field.value?.toString() || "__none__"}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select parent" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {jurisdictions
                          .filter(j => editingJurisdiction ? j.id !== editingJurisdiction.id : true)
                          .map(jurisdiction => (
                            <SelectItem key={jurisdiction.id} value={jurisdiction.id.toString()}>
                              {jurisdiction.name}
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      For hierarchical jurisdictions (e.g. a city within a state)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={jurisdictionForm.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Active</FormLabel>
                      <FormDescription>
                        Inactive jurisdictions won't be used for tax calculations
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button type="submit" disabled={createJurisdictionMutation.isPending || updateJurisdictionMutation.isPending}>
                  {(createJurisdictionMutation.isPending || updateJurisdictionMutation.isPending) && (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {editingJurisdiction ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Tax Rate Dialog */}
      <Dialog open={taxRateDialogOpen} onOpenChange={setTaxRateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingTaxRate ? "Edit Tax Rate" : "Add Tax Rate"}
            </DialogTitle>
            <DialogDescription>
              Enter the details for the tax rate.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...taxRateForm}>
            <form onSubmit={taxRateForm.handleSubmit(onSubmitTaxRate)} className="space-y-4">
              <FormField
                control={taxRateForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rate Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. California State Sales Tax" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={taxRateForm.control}
                name="rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tax Rate (%)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 7.25" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={taxRateForm.control}
                name="jurisdictionId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Jurisdiction</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(parseInt(value))} 
                      value={field.value?.toString() || ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select jurisdiction" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {jurisdictions.map(jurisdiction => (
                          <SelectItem key={jurisdiction.id} value={jurisdiction.id.toString()}>
                            {jurisdiction.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={taxRateForm.control}
                  name="effectiveDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Effective Date</FormLabel>
                      <FormControl>
                        <DatePicker
                          date={field.value}
                          onDateChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={taxRateForm.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date (Optional)</FormLabel>
                      <FormControl>
                        <DatePicker
                          date={field.value || undefined}
                          onDateChange={field.onChange}
                          placeholder="No end date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={taxRateForm.control}
                name="isCompound"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Compound Tax</FormLabel>
                      <FormDescription>
                        Compound taxes are calculated on top of other taxes
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              
              <FormField
                control={taxRateForm.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Active</FormLabel>
                      <FormDescription>
                        Inactive tax rates won't be used for calculations
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
              
              <FormField
                control={taxRateForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        {...field} 
                        value={field.value ?? ""}
                        placeholder="Additional information about this tax rate" 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button type="submit" disabled={createTaxRateMutation.isPending || updateTaxRateMutation.isPending}>
                  {(createTaxRateMutation.isPending || updateTaxRateMutation.isPending) && (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {editingTaxRate ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesTaxPage;
